/**
 * Status of one enrollment task. Mirrors Core.EnrollmentTaskStatus.
 *
 * The three terminal values all mean "stop waiting on this" — a skipped task and one that never
 * applied both release whatever was blocked behind them. Use `isTerminalTaskStatus` rather than
 * testing the values inline.
 */
export enum EnrollmentTaskStatus {
	Locked = 1,
	Available = 2,
	InProgress = 3,
	Complete = 4,
	Skipped = 5,
	NotApplicable = 6
}

export function isTerminalTaskStatus(status: EnrollmentTaskStatus): boolean {
	return (
		status === EnrollmentTaskStatus.Complete ||
		status === EnrollmentTaskStatus.Skipped ||
		status === EnrollmentTaskStatus.NotApplicable
	);
}

/** Whether a task exists once per enrollment or once per semester. */
export enum EnrollmentTaskScope {
	Enrollment = 1,
	Semester = 2
}

/**
 * Who a task is waiting on.
 *
 * Registrar is not a role in the system — it is a label for work the registrar group picks up, and
 * access to that work is the `Admin:EnrollmentTask:Manage` entitlement. Never make an access
 * decision from this value; it exists so the UI can say who is being waited on.
 */
export enum EnrollmentTaskOwnerRole {
	System = 1,
	Learner = 2,
	Instructor = 3,
	Registrar = 4
}

/**
 * What a timed task counts its offset from.
 *
 * Only ever meaningful alongside an offset in days — an anchor with no days would make the task due
 * the instant it opened, and days with no anchor are never applied by any pass at all. The config
 * editor requires both or neither for that reason.
 *
 * `TaskAvailable` counts from when this step itself opens.
 */
export enum EnrollmentTaskOffsetAnchor {
	LastLessonAttendance = 1,
	EnrollmentCompletedDate = 2,
	TaskAvailable = 3
}

/** Rollup state of a whole checklist. Derived server-side on read. */
export enum EnrollmentChecklistStatus {
	NotStarted = 1,
	InProgress = 2,
	Complete = 3
}
